import { Routes } from '@angular/router';
import { Login } from './auth/login/login';
import { ForgotPassword } from './auth/forgot-password/forgot-password';
import { ResetPassword } from './auth/reset-password/reset-password';
import { TwoFactorAuth } from './auth/two-factor-auth/two-factor-auth';
import { SubscriptionInactiveComponent } from './auth/subscription-inactive/subscription-inactive.component';
import { AdminLayout } from './layout/admin-layout/admin-layout';
import { OnlineHandbook } from './features/online-handbook/online-handbook';
import { RoleManagement } from './features/role/role-management/role-management';
import { BaseRoleManagement } from './features/role/base-role-management/base-role-management';
import { ViewUser } from './features/user/view-user/view-user';
import { AddEditUser } from './features/user/add-edit-user/add-edit-user';
import { UserAccountSetup } from './features/user/user-account-setup/user-account-setup';
import { UserProfile } from './features/user/user-profile/user-profile';
import { AccountPendingApproval } from './features/user/account-pending-approval/account-pending-approval';
import { TestMap } from './features/map/test-map/test-map';
import { CompanyConfig } from './features/company-config/company-config';
import { SubscriptionComponent } from './features/chargebee/subscription/subscription.component';
import { PortalComponent } from './features/chargebee/portal/portal.component';
import { AuthGuard } from './core/guards/auth.guard';
import { PermissionGuard } from './core/guards/permission.guard';
import { UnsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { ViewSubscribers } from './features/subscribers/view-subscribers/view-subscribers';
import { TerritoryManagementComponent } from './features/territories/territory-management/territory-management';
import { AddTerritoryComponent } from './features/territories/add-territory/add-territory';
import { SignUp } from './auth/sign-up/sign-up';
import { MessageTemplate } from './features/message-template/message-template';
import { PageNotFound } from './features/page-not-found/page-not-found';
import { SystemLogs } from './features/system-logs/system-logs';
import { ProductManagement } from './features/products/product-management/product-management';
import { AddEditProduct } from './features/products/add-edit-product/add-edit-product';
import { Calendar } from './features/calendar/calendar';
import { ChargebeeSuccess } from './features/chargebee/chargebee-success/chargebee-success';
import { AddEditCustomer } from './features/customer/add-edit-customer/add-edit-customer';
import { ViewCustomer } from './features/customer/view-customer/view-customer';
import { AddReservation } from './features/reservation/add-reservation/add-reservation';
import { CustomerLookup } from './features/customer/customer-lookup/customer-lookup';
import { SheduleInteraction } from './features/interaction/shedule-interaction/shedule-interaction';
import { LogInteraction } from './features/interaction/log-interaction/log-interaction';
import { ViewReservation } from './features/reservation/view-reservation/view-reservation';
import { CustomerComponent } from './features/customer/customer';
import { AddEditContract } from './features/contract/add-edit-contract/add-edit-contract';
import { ContractComponent } from './features/contract/contract';
import { AdminDashboard } from './features/dashboard/admin-dashboard/admin-dashboard';
import { FsrDashboard } from './features/dashboard/fsr-dashboard/fsr-dashboard';
import { ReservationList } from './features/reservation/reservation-list/reservation-list';
import { InteractionList } from './features/interaction/interaction-list/interaction-list';
import { CustomReports } from './features/custom-reports/custom-reports';
import { SimpleReports } from './features/simple-reports/simple-reports';
import { UnauthorizedComponent } from './features/unauthorized/unauthorized';
import { NotificationsPageComponent } from './features/notifications/notifications-page.component';


export const routes: Routes = [
  {
    path: '',
    component: Login,
  },
  {
    path: 'sign-up',
    component: SignUp,
  },
  {
    path: 'forgot-password',
    component: ForgotPassword,
  },
  {
    path: 'reset-password/:token',
    component: ResetPassword,
  },
  {
    path: 'two-factor-auth',
    component: TwoFactorAuth,
  },
  {
    path: 'two-factor-auth/:email',
    component: TwoFactorAuth,
  },
  {
    path: 'user-account-setup',
    component: UserAccountSetup,
  },
  {
    path: 'account-pending-approval',
    component: AccountPendingApproval,
  },
  {
    path: 'subscription-inactive',
    component: SubscriptionInactiveComponent,
  },
  {
    path: 'test-map',
    component: TestMap,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin',
    component: AdminLayout,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        redirectTo: '/admin/admin-dashboard',
        pathMatch: 'full',
      },
      {
        path: 'admin-dashboard',
        component: AdminDashboard,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['dashboard.admin.view'],
          requireAll: false
        }
      },
      {
        path: 'fsr-dashboard',
        component: FsrDashboard,
        canActivate: [PermissionGuard],
        data: { 
          roles: ['FSR'],
          requireAll: false
        }
      },
      {
        path: 'online-handbook',
        component: OnlineHandbook,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['handbook.read'],
          requireAll: false
        }
      },
      {
        path: 'role-management',
        component: RoleManagement,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['role.read'],
          requireAll: false
        }
      },
      {
        path: 'base-role-management',
        component: BaseRoleManagement,
        canActivate: [PermissionGuard],
        data: { 
          roles: ['SUPER_DEV_ADMIN'],
          requireAll: false
        }
      },
      {
        path: 'user-management',
        component: ViewUser,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['user.read'],
          requireAll: false
        }
      },
      {
        path: 'add-edit-user',
        component: AddEditUser,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['user.create', 'user.update'],
          requireAll: false
        }
      },
      {
        path: 'user-account-setup',
        component: UserAccountSetup,
      },
      {
        path: 'me',
        component: UserProfile,
      },
      {
        path: 'company-configuration',
        component: CompanyConfig,
        canActivate: [PermissionGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: { 
          permissions: ['subscriber.update'],
          requireAll: false
        }
      },
      {
        path: 'payment/portal',
        component: PortalComponent,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['billing.portal.access'],
          requireAll: false
        }
      },
      {
        path: 'subscriber-management',
        component: ViewSubscribers,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['subscriber.read'],
          requireAll: false
        }
      },
      {
        path: 'product-management',
        component: ProductManagement,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['product.read'],
          requireAll: false
        }
      },
      {
        path: 'add-edit-product',
        component: AddEditProduct,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['product.create', 'product.update'],
          requireAll: false
        }
      },
      {
        path: 'territory-management',
        component: TerritoryManagementComponent,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['territory.read'],
          requireAll: false
        }
      },
      {
        path: 'territory-management/add',
        component: AddTerritoryComponent,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['territory.create'],
          requireAll: false
        }
      },
      {
        path: 'message-template',
        component: MessageTemplate,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['messagetemplate.read'],
          requireAll: false
        }
      },
      {
        path: 'system-logs',
        component: SystemLogs,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['systemlog.read'],
          requireAll: false
        }
      },
      {
        path: 'custom-reports',
        component: CustomReports,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['reports.read'],
          requireAll: false
        }
      },
      {
        path: 'simple-reports',
        component: SimpleReports,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['reports.read'],
          requireAll: false
        }
      },
      {
        path: 'notifications',
        component: NotificationsPageComponent,
        canActivate: [PermissionGuard],
        data: { 
          // All authenticated users can see notifications
          requireAll: false
        }
      },
      {
        path: 'calendar',
        component: Calendar,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['calendar.event.read'],
          requireAll: false
        }
      },
      {
        path: 'customer-lookup',
        component: CustomerLookup,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['customer.lookup'],
          requireAll: false
        }
      },
      {
        path: 'add-edit-customer',
        component: AddEditCustomer,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['customer.create', 'customer.update'],
          requireAll: false
        }
      },
      {
        path: 'view-customer/:id',
        component: ViewCustomer,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['customer.read'],
          requireAll: false
        }
      },
      {
        path: 'customers',
        component: CustomerComponent,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['customer.read'],
          requireAll: false
        }
      },
      {
        path: 'add-reservation',
        component: AddReservation,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['reservation.create'],
          requireAll: false
        }
      },
      {
        path: 'view-reservation/:id',
        component: ViewReservation,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['reservation.read'],
          requireAll: false
        }
      },
      {
        path: 'reservations',
        component: ReservationList,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['reservation.read'],
          requireAll: false
        }
      },
      {
        path: 'shedule-interaction/:id',
        component: SheduleInteraction,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['interaction.create'],
          requireAll: false
        }
      },
      {
        path: 'log-interaction/update/:interactionId',
        component: LogInteraction,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['interaction.update'],
          requireAll: false
        }
      },
      {
        path: 'log-interaction/create/:reservationId',
        component: LogInteraction,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['interaction.create'],
          requireAll: false
        }
      },
      {
        path: 'interactions',
        component: InteractionList,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['interaction.read'],
          requireAll: false
        }
      },
      {
        path: 'add-edit-contract',
        component: AddEditContract,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['contract.create', 'contract.update'],
          requireAll: false
        }
      },
      {
        path: 'add-edit-contract/update/:id',
        component: AddEditContract,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['contract.update'],
          requireAll: false
        }
      },
      {
        path: 'add-edit-contract/:reservationId',
        component: AddEditContract,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['contract.create'],
          requireAll: false
        }
      },
      {
        path: 'contract-management',
        component: ContractComponent,
        canActivate: [PermissionGuard],
        data: { 
          permissions: ['contract.read'],
          requireAll: false
        }
      },
      {
        path: 'unauthorized',
        component: UnauthorizedComponent
      },

      
    ],
  },
  {
    path: 'payment',
    // canActivate: [AuthGuard],
    children: [
      {
        path: '',
        redirectTo: '/payment/sub',
        pathMatch: 'full',
      },
      {
        path: 'sub',
        component: SubscriptionComponent,
      },
      {
        path: 'portal',
        component: PortalComponent,
      },
      {
        path: 'payment-success',
        component: ChargebeeSuccess
      }
    ],
  },
  {
    path: '**',
    pathMatch: 'full',
    component: PageNotFound
  },

];
